import providers from "./providers";

const STORAGE_PREFIX = "aisensy_px:";
const STORAGE_TTL_MS = 24 * 60 * 60 * 1000;
// Locked in localStorage too, so a refresh or a second tab can't send them again.
const STORED_EVENTS = ["Lead", "InitiateCheckout", "Purchase"];

const storage = {
  has(key) {
    try {
      return window.localStorage.getItem(key) !== null;
    } catch (e) {
      return false;
    }
  },
  save(key) {
    try {
      window.localStorage.setItem(key, String(Date.now()));
    } catch (e) {
      // Storage blocked: the in-memory lock still applies.
    }
  },
  pruneOld() {
    try {
      const now = Date.now();
      for (let i = window.localStorage.length - 1; i >= 0; i--) {
        const key = window.localStorage.key(i);
        if (!key || !key.startsWith(STORAGE_PREFIX)) continue;
        const savedAt = Number(window.localStorage.getItem(key));
        if (!savedAt || now - savedAt > STORAGE_TTL_MS) window.localStorage.removeItem(key);
      }
    } catch (e) {
      // Nothing to clean when storage is blocked.
    }
  },
};

const providerFor = (pixel) =>
  pixel && Object.prototype.hasOwnProperty.call(providers, pixel.provider)
    ? providers[pixel.provider]
    : null;

// Sends our four events (PageView, Lead, InitiateCheckout, Purchase) to every pixel, once each.
export function createTracker(pixels) {
  const groups = {};
  (Array.isArray(pixels) ? pixels : []).forEach((pixel) => {
    const provider = providerFor(pixel);
    // The server validates IDs; checked again here because they go into fbq()/gtag() calls.
    if (!provider || typeof pixel.id !== "string" || !provider.isValid(pixel)) return;
    (groups[pixel.provider] = groups[pixel.provider] || []).push(pixel);
  });
  const active = Object.keys(groups);
  const fired = new Set();

  if (active.length) storage.pruneOld();
  active.forEach((name) => {
    try {
      providers[name].load(groups[name]);
    } catch (e) {
      console.error(`tracking: ${name} failed to load`, e);
    }
  });

  const emit = (event, data = {}, key = "") => {
    if (!active.length) return;
    const lockKey = `${event}:${key}`;
    if (fired.has(lockKey)) return;
    fired.add(lockKey);

    const storageKey = STORAGE_PREFIX + lockKey;
    const useStorage = STORED_EVENTS.includes(event) && !!key;
    if (useStorage) {
      if (storage.has(storageKey)) return;
      storage.save(storageKey);
    }

    active.forEach((name) => {
      groups[name].forEach((pixel) => {
        try {
          providers[name].send(event, data, key, pixel);
        } catch (e) {
          console.error(`tracking: ${name} failed to send ${event}`, e);
        }
      });
    });
  };

  return { emit };
}
